import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">你访问的分类不存在或已被移除</p>
            <button
              onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const baseUrl = typeof window !== 'undefined' ? window.location.origin + (window as any).__BASE_PATH__ : '';
  const canonicalUrl = baseUrl + '/category/' + slug;

  const handleClick = (articleSlug: string) => {
    navigate(`/article/${articleSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (catSlug: string) => {
    navigate(`/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 麻将竞技资讯`}
        description={category.description}
        keywords={`${category.name},麻将爆奖,麻将中大奖,麻将牌型技巧,麻将竞技娱乐`}
        canonicalUrl={canonicalUrl}
      />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <article
                    key={article.slug}
                    onClick={() => handleClick(article.slug)}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer hover:border-primary-200 transition-all"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.author}</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}